import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import { notificationsApi } from '@/services/notifications.service';
import { rentalsApi } from '@/services/rentals.service';
import type { Campaign, CampaignListResult } from '@/types/notification.types';
import { campaignFormSchema } from '@/validators/notifications.validator';

const PAGE_SIZE = 10;
const CAMPAIGNS_QUERY_KEY = 'campaigns';

export type ComposeScope = 'all' | 'campus';
export type ScheduleMode = 'now' | 'later';

export interface CampaignForm {
  title: string;
  message: string;
  scope: ComposeScope;
  campusIds: string[];
  scheduleMode: ScheduleMode;
  sendAt: string;
}

export type CampaignFieldError = 'title' | 'message' | 'campusIds' | 'sendAt';

const EMPTY_FORM: CampaignForm = {
  title: '',
  message: '',
  scope: 'all',
  campusIds: [],
  scheduleMode: 'now',
  sendAt: '',
};

const extractApiError = (error: unknown): string | null =>
  (error as { response?: { data?: { error?: { message?: string } } } })?.response?.data?.error
    ?.message ?? null;

interface UseNotificationsPageResult {
  form: CampaignForm;
  errors: Partial<Record<CampaignFieldError, string>>;
  submitError: string | null;
  successMessage: string | null;
  isSending: boolean;
  campusOptions: { id: string; name: string }[];
  campaigns: Campaign[];
  page: number;
  totalPages: number;
  isLoadingList: boolean;
  onChangeField: (field: 'title' | 'message', value: string) => void;
  onChangeScope: (scope: ComposeScope) => void;
  onToggleCampus: (campusId: string) => void;
  /** Bulk helpers — picking 30 colleges one chip at a time is not a workflow. */
  onSelectAllCampuses: () => void;
  onClearCampuses: () => void;
  onChangeScheduleMode: (mode: ScheduleMode) => void;
  onChangeSendAt: (value: string) => void;
  onChangePage: (page: number) => void;
  onSubmit: () => void;
}

export const useNotificationsPage = (): UseNotificationsPageResult => {
  const queryClient = useQueryClient();

  const [form, setForm] = useState<CampaignForm>(EMPTY_FORM);
  const [errors, setErrors] = useState<Partial<Record<CampaignFieldError, string>>>({});
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [page, setPage] = useState(1);

  const campusOptionsQuery = useQuery({
    queryKey: [CAMPAIGNS_QUERY_KEY, 'campus-options'],
    queryFn: () => rentalsApi.listCampusOptions(),
    staleTime: 5 * 60 * 1000,
  });

  const listQuery = useQuery<CampaignListResult>({
    queryKey: [CAMPAIGNS_QUERY_KEY, 'list', page],
    queryFn: () => notificationsApi.list(page, PAGE_SIZE),
    placeholderData: (previous) => previous,
  });

  const sendMutation = useMutation<Campaign, unknown, void>({
    mutationFn: () => {
      const sendAtIso =
        form.scheduleMode === 'later' && form.sendAt
          ? new Date(form.sendAt).toISOString()
          : undefined;
      return notificationsApi.send({
        title: form.title.trim(),
        message: form.message.trim(),
        audience: {
          scope: form.scope,
          campusIds: form.scope === 'campus' ? form.campusIds : [],
        },
        sendAt: sendAtIso,
      });
    },
    onSuccess: (campaign) => {
      setForm(EMPTY_FORM);
      setSuccessMessage(
        campaign.status === 'scheduled'
          ? 'Campaign scheduled.'
          : `Campaign sent to ${campaign.recipients.toLocaleString()} user${campaign.recipients === 1 ? '' : 's'}.`,
      );
      void queryClient.invalidateQueries({ queryKey: [CAMPAIGNS_QUERY_KEY, 'list'] });
    },
    onError: (error) => setSubmitError(extractApiError(error) ?? 'Could not send the campaign.'),
  });

  const onSubmit = (): void => {
    setSubmitError(null);
    setSuccessMessage(null);

    const parsed = campaignFormSchema.safeParse(form);
    if (!parsed.success) {
      const fieldErrors: Partial<Record<CampaignFieldError, string>> = {};
      for (const issue of parsed.error.issues) {
        const field = issue.path[0];
        if (
          field === 'title' ||
          field === 'message' ||
          field === 'campusIds' ||
          field === 'sendAt'
        ) {
          fieldErrors[field] = fieldErrors[field] ?? issue.message;
        }
      }
      setErrors(fieldErrors);
      return;
    }

    setErrors({});
    sendMutation.mutate();
  };

  return {
    form,
    errors,
    submitError,
    successMessage,
    isSending: sendMutation.isPending,
    campusOptions: campusOptionsQuery.data ?? [],
    campaigns: listQuery.data?.items ?? [],
    page: listQuery.data?.page ?? page,
    totalPages: listQuery.data?.totalPages ?? 1,
    isLoadingList: listQuery.isLoading,
    onChangeField: (field, value) => {
      setErrors((prev) => ({ ...prev, [field]: undefined }));
      setSuccessMessage(null);
      setForm((prev) => ({ ...prev, [field]: value }));
    },
    onChangeScope: (scope) => setForm((prev) => ({ ...prev, scope })),
    onToggleCampus: (campusId) =>
      setForm((prev) => ({
        ...prev,
        campusIds: prev.campusIds.includes(campusId)
          ? prev.campusIds.filter((id) => id !== campusId)
          : [...prev.campusIds, campusId],
      })),
    onSelectAllCampuses: () => {
      setErrors((prev) => ({ ...prev, campusIds: undefined }));
      setForm((prev) => ({
        ...prev,
        campusIds: (campusOptionsQuery.data ?? []).map((campus) => campus.id),
      }));
    },
    onClearCampuses: () => setForm((prev) => ({ ...prev, campusIds: [] })),
    onChangeScheduleMode: (scheduleMode) => setForm((prev) => ({ ...prev, scheduleMode })),
    onChangeSendAt: (sendAt) => setForm((prev) => ({ ...prev, sendAt })),
    onChangePage: setPage,
    onSubmit,
  };
};
